<template>
    <div class="modal-container">
        <div class="modal-header">
            <h4 clas="modal-title">Edit {{ assetGroupData.groupName }}</h4>
            <md-button class="md-simple md-just-icon md-round modal-default-button" @click.stop="$modal.hide">
                <md-icon>clear</md-icon>
            </md-button>
        </div>
        <div class="modal-body">
            <form-wrapper :validator="$v.assetGroup" class="form-wrapper">
                <div class="md-layout">
                    <div class="md-layout-item md-size-100">
                        <form-group label="Asset Group Name*" name="groupName">
                            <md-input v-model="assetGroup.groupName" />
                        </form-group>
                    </div>
                    <div class="md-layout-item md-size-100">
                        <form-group v-if="showTeamRegion" name="teamRegion" label="Team Region">
                            <team-region-options
                                :selected="assetGroup.teamRegionId"
                                @selectedOption="handleTeamRegionChanged"
                                mode="update"
                                :show-auto="false"
                            />
                        </form-group>
                    </div>
                    <div class="md-layout-item md-size-50 icon-field">
                        <form-group label="Vehicle / Asset Icon" name="icon">
                            <md-select v-model="assetGroup.iconLabel">
                                <md-option v-for="(icon, index) in icons" :key="index" :value="icon.label">
                                    {{ icon.label }}
                                    <md-tooltip md-direction="right">
                                        <span class="material-icons" :style="{ color: getIconColour() }">
                                            {{ icon.name }}
                                        </span>
                                    </md-tooltip>
                                </md-option>
                            </md-select>
                            <span class="material-icons display-icon" :style="{ color: getIconColour() }">
                                {{ assetGroup.icon }}
                            </span>
                        </form-group>
                    </div>
                    <div class="md-layout-item md-size-50 color-form-group">
                        <form-group name="iconColour" label="Colour">
                            <md-input v-model="assetGroup.iconColour" />
                        </form-group>
                        <input type="color" v-model="assetGroup.iconColour" class="color-picker" />
                    </div>
                </div>
            </form-wrapper>
        </div>
        <div class="modal-footer">
            <md-button class="md-primary dialog-button" @click="update()">
                Update
            </md-button>
            <md-button class="md-default dialog-button" @click.stop="$modal.hide">
                Cancel
            </md-button>
        </div>
    </div>
</template>

<script>
import { TeamRegionOptions } from '@/components';
import { ASSET_GROUP_ICONS_CONSTANTS, ASSET_GROUP_ICONS_DEFAULT_COLOUR } from '@/utils/constants';
import { required } from 'vuelidate/lib/validators';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showTeamRegionControl } from '@/helpers';
import { mapGetters } from 'vuex';

export default {
    name: 'UpdateAssetGroup',
    mixins: [GeneralMixin],
    props: {
        assetGroupData: {
            type: Object,
            default: () => {}
        },
        resolve: {
            type: Function,
            default: () => {}
        }
    },
    components: {
        TeamRegionOptions
    },
    mounted() {
        this.assetGroup.groupName = this.assetGroupData.groupName;
        this.assetGroup.teamRegionId = this.assetGroupData.teamRegionId;
        this.assetGroup.iconColour = this.assetGroupData.iconColour;
        if (this.assetGroupData.icon) {
            const iconData = this.icons.find((x) => x.name === this.assetGroupData.icon);
            this.assetGroup.icon = iconData?.name;
            this.assetGroup.iconLabel = iconData?.label;
        } else {
            // set the default icon
            const iconData = this.icons.find((x) => x.name === 'directions_car');
            this.assetGroup.icon = iconData?.name;
            this.assetGroup.iconLabel = iconData?.label;
        }
    },
    data() {
        return {
            assetGroup: {
                groupName: null,
                teamRegionId: null,
                icon: null,
                iconLabel: null,
                iconColour: null
            },
            icons: ASSET_GROUP_ICONS_CONSTANTS,
            defaultIconColour: ASSET_GROUP_ICONS_DEFAULT_COLOUR
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user',
            hasAssetAccess: 'user/hasAssetAccess',
            teamRegions: 'team/teamRegions',
            isSingleUser: 'user/isIndividualUser',
            isSingleTeamMember: 'team/isSingleTeamMember'
        }),
        showTeamRegion() {
            return showTeamRegionControl(
                {
                    user: this.user,
                    hasAssetAccess: this.hasAssetAccess,
                    teamRegions: this.teamRegions
                },
                this.assetGroup.teamRegionId
            );
        }
    },
    validations: {
        assetGroup: {
            groupName: {
                required
            }
        }
    },
    methods: {
        async update() {
            this.$v.assetGroup.$touch();
            const isValid = !this.$v.assetGroup.$invalid;

            if (isValid) {
                this.$_handleLoaderState(true, 'SAVING...');
                const api = `/api/asset-groups/${this.assetGroupData.assetGroupId}`;

                const data = Object.assign({}, this.assetGroupData, this.assetGroup);

                // const api = '/api/assets';
                const payload = {
                    method: 'put',
                    data
                };

                try {
                    await handleRequests(api, payload);

                    this.$notify({
                        message: 'Successfully updated the asset group.',
                        type: 'success'
                    });

                    this.resolve(data);

                    // TODO: We need to add a way to clear the customfields after successfully creating the asset.
                    this.$v.$reset();
                } catch (error) {
                    const err = error.data;
                    if (err && err.length) {
                        err.forEach((e) => {
                            this.$notify({
                                message: e.message,
                                type: 'danger'
                            });
                        });
                    }
                }
            }

            this.$_handleLoaderState(false);
        },
        handleTeamRegionChanged(value) {
            this.assetGroup.teamRegionId = value;
        },
        getIconColour() {
            return this.assetGroup.iconColour ?? this.defaultIconColour;
        }
    },
    watch: {
        'assetGroup.iconLabel': function(newVal, oldVal) {
            if (newVal && newVal !== oldVal) {
                const iconName = this.icons.find((x) => x.label === newVal)?.name;
                this.assetGroup.icon = iconName;
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.icon-field {
    .md-icon {
        color: inherit !important;
    }
}

.color-form-group {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.color-picker {
    border: none;
    padding: 0;
    margin: 0;
}

.tooltip-icon {
    color: inherit !important;
}

.display-icon {
    padding-top: 5px;
}
</style>
